import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, Minus, Check, Star, Flame } from "lucide-react";
import { inr } from "./BillingParts";

export default function ItemPickerSheet({ open, onOpenChange, stockItems, lines, onAdd, onSetQty, onFavorite }) {
  const [q, setQ] = useState("");
  const qtyOf = (id) => { const l = lines.find(x => x.item_id === id); return l ? Number(l.qty) || 0 : 0; };
  const s = q.trim().toLowerCase();
  const match = (it) => !s || it.name.toLowerCase().includes(s) || (it.sku || "").toLowerCase().includes(s) || (it.barcode || "").includes(s) || (it.category || "").toLowerCase().includes(s);
  const { favs, hot, rest } = useMemo(() => {
    const m = stockItems.filter(match);
    const favs = m.filter(i => i.favorite);
    const hot = m.filter(i => !i.favorite && i.auto_favorite).sort((a, b) => (b.bill_count_30d || 0) - (a.bill_count_30d || 0));
    return { favs, hot, rest: m.filter(i => !i.favorite && !i.auto_favorite) };
  }, [q, stockItems]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = lines.filter(l => l.item_id && (Number(l.qty) || 0) > 0).length;

  const toggleFav = async (it) => {
    try { const { data } = await api.post(`/inventory/items/${it.id}/favorite`); onFavorite?.(it.id, data.favorite); } catch { /* ignore */ }
  };

  const Row = (it) => {
    const n = qtyOf(it.id);
    return (
      <div key={it.id} data-testid={`item-picker-row-${it.id}`} className={`flex items-center gap-2 px-3 py-3 ${n ? "bg-zinc-50" : ""}`}>
        <button type="button" data-testid={`item-picker-fav-${it.id}`} onClick={() => toggleFav(it)} aria-label={it.favorite ? "Unpin" : "Pin to top"} className={`h-9 w-9 shrink-0 flex items-center justify-center ${it.favorite ? "text-amber-500" : "text-zinc-300 active:text-amber-400"}`}>
          <Star size={17} fill={it.favorite ? "currentColor" : "none"} />
        </button>
        <button type="button" onClick={() => onAdd(it)} className="flex-1 min-w-0 text-left">
          <div className="text-sm font-semibold truncate">{it.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{inr(it.sale_price)}{it.type === "product" ? ` · ${it.total_stock} ${it.unit} in stock` : " · service"}{it.sku ? ` · ${it.sku}` : ""}{it.bill_count_30d ? ` · billed ${it.bill_count_30d}× in 30d` : ""}</div>
        </button>
        {n ? (
          <div className="flex items-center border border-zinc-300 bg-white">
            <button type="button" data-testid={`item-picker-dec-${it.id}`} onClick={() => onSetQty(it, n - 1)} className="h-9 w-9 flex items-center justify-center active:bg-zinc-100"><Minus size={14} /></button>
            <span className="w-8 text-center text-sm tabular font-semibold" data-testid={`item-picker-qty-${it.id}`}>{n}</span>
            <button type="button" data-testid={`item-picker-inc-${it.id}`} onClick={() => onSetQty(it, n + 1)} className="h-9 w-9 flex items-center justify-center active:bg-zinc-100"><Plus size={14} /></button>
          </div>
        ) : (
          <button type="button" data-testid={`item-picker-add-${it.id}`} onClick={() => onAdd(it)} className="h-9 px-3 border border-zinc-900 text-xs font-semibold flex items-center gap-1 active:bg-zinc-900 active:text-white"><Plus size={13} /> Add</button>
        )}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[88dvh] p-0 flex flex-col rounded-t-2xl" data-testid="item-picker-sheet">
        <SheetHeader className="px-4 pt-4 pb-2 text-left">
          <SheetTitle className="font-heading text-base">Add items</SheetTitle>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="item-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, barcode…" className="h-10 pl-9 rounded-none" />
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto" data-testid="item-picker-list">
          {favs.length ? (
            <>
              <div className="px-4 py-1.5 bg-amber-50 text-[10px] tracking-[0.12em] uppercase text-amber-700 font-semibold flex items-center gap-1" data-testid="item-picker-favs-header"><Star size={11} fill="currentColor" /> Pinned favourites</div>
              <div className="divide-y divide-zinc-100">{favs.map(Row)}</div>
            </>
          ) : null}
          {hot.length ? (
            <>
              <div className="px-4 py-1.5 bg-orange-50 text-[10px] tracking-[0.12em] uppercase text-orange-700 font-semibold flex items-center gap-1" data-testid="item-picker-hot-header"><Flame size={11} fill="currentColor" /> Frequently billed · last 30 days</div>
              <div className="divide-y divide-zinc-100">{hot.map(Row)}</div>
            </>
          ) : null}
          {(favs.length || hot.length) && rest.length ? <div className="px-4 py-1.5 bg-zinc-50 text-[10px] tracking-[0.12em] uppercase text-zinc-500 font-semibold">All items</div> : null}
          {favs.length + hot.length + rest.length === 0 ? <div className="p-6 text-center text-sm text-zinc-500">No items match “{q}”</div> : null}
          <div className="divide-y divide-zinc-100">{rest.map(Row)}</div>
          {!stockItems.some(i => i.favorite) && !s ? <div className="px-4 py-3 text-[11px] text-zinc-400 text-center">Tap the star to pin frequently billed items to the top</div> : null}
        </div>
        <div className="shrink-0 border-t border-zinc-200 px-4 py-3 bg-white" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <Button data-testid="item-picker-done" onClick={() => onOpenChange(false)} className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"><Check size={15} className="mr-1" /> Done{picked ? ` · ${picked} item${picked > 1 ? "s" : ""}` : ""}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
